<template>
    <div class="container-nzidoo">
        <page-title title="Liste des demandes de paiements" />

        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>

                <input type="text" v-model="searchTerm" placeholder="Rechercher par nom d'utilisateur"
                    class="admin-search-input">

                <!-- Clear icon -->
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
                    title="Effacer"></i>
            </div>

            <div class="admin-toolbar-info">
                <span class="toolbar-stat">
                    <i class="mdi mdi-wallet"></i>
                    Total: {{ wallets.length }} portefeuille(s)
                </span>
            </div>
        </div>

        <LoadingSpinner v-if="loader" />
        <!-- Wallets Grid -->
        <div v-else class="admin-grid">
            <div v-for="wallet in filteredWallets" :key="wallet.id" class="admin-card">
                <div class="admin-card-header">
                    <div class="wallet-user-info">
                        <img :src="wallet.user.picture ? storageUrl + wallet.user.picture : img_not_found" alt=""
                            class="contact-avatar">
                        <div>
                            <span class="chatbot-card-id">
                                {{ wallet.user.lastname + " " + wallet.user.firstname }}
                            </span>
                            <span class="user-email" v-if="wallet.user.email">{{ wallet.user.email }}</span>
                        </div>
                    </div>
                    <div class="admin-card-actions">
                        <button class="btn btn-icon-action btn-success" @click="showPaymentModal(wallet.id)"
                            title="Payer">
                            <i class="mdi mdi-cash"></i>
                        </button>
                    </div>
                </div>
                <div class="admin-card-body">
                    <div class="wallet-stats">
                        <div class="wallet-stat-item">
                            <label class="admin-card-label">Revenue</label>
                            <div class="stat-value revenue-value">
                                {{
                                    (wallet.total_amount_won - wallet.total_amount_won * percentage)
                                    | currency(" DA", 0, { symbolOnLeft: false })
                                }}
                            </div>
                        </div>
                        <div class="wallet-stat-item">
                            <label class="admin-card-label">Balance</label>
                            <div class="stat-value balance-value">
                                {{
                                    wallet.unpaid_amount
                                    | currency(" DA", 0, { symbolOnLeft: false })
                                }}
                            </div>
                        </div>
                    </div>
                    <div class="admin-card-section">
                        <label class="admin-card-label">État</label>
                        <div v-if="pendingPaymentRequest(wallet.payment_request) === 0"
                            class="status-badge status-success">
                            <i class="mdi mdi-check-circle"></i>
                            Aucune demande en cours
                        </div>
                        <div v-else class="status-badge status-warning">
                            <i class="mdi mdi-clock-outline"></i>
                            {{ pendingPaymentRequest(wallet.payment_request) }} demande(s) en cours
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredWallets.length === 0 && wallets.length > 0" class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="admin-empty-text">Aucun résultat trouvé</p>
            </div>

            <!-- No Data State -->
            <div v-if="wallets.length === 0" class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                    <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
                <p class="admin-empty-text">Aucune demande n'a encore été faite</p>
            </div>
        </div>

        <make-payment-modal 
            v-if="false !== selectedWalletId" 
            :walletId="selectedWalletId"
            @closed="onModalClosed">
        </make-payment-modal>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import MakePaymentModal from "@/components/modals/MakePaymentModal.vue";
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import config from "@/config";
import img_not_found from "@/assets/img/img_not_found.jpg";

export default {
    components: {
        PageTitle,
        LoadingSpinner,
        "make-payment-modal": MakePaymentModal
    },
    name: "PaymentRequestsAdmin",

    data() {
        return {
            wallets: [],

            storageUrl: config.storageUrl,
            img_not_found: img_not_found,
            selectedWalletId: false,
            loader: false,
            searchTerm: "",
            percentage: config.accountingPercentage,
        };
    },

    computed: {
        filteredWallets() {
            if (!this.searchTerm) return this.wallets;

            const term=this.searchTerm.toLowerCase();
            return this.wallets.filter(wallet => {
                const fullName=(wallet.user.lastname+" "+wallet.user.firstname).toLowerCase();
                const email=wallet.user.email? wallet.user.email.toLowerCase():"";
                return fullName.includes(term)||email.includes(term);
            });
        }
    },

    mounted() {
        this.getWallets();
    },

    methods: {
        clearSearch() {
            this.searchTerm="";
        },

        async getWallets() {
            try {
                this.loader=true;
                const response=await this.$axios.get("admin/wallet/all");
                this.wallets=response.data;
                this.loader=false;
            } catch (err) {
                console.error("Error loading wallets:", err);
                this.$notify({
                    group: "error",
                    title: "Erreur lors du chargement des portefeuilles",
                    type: "error",
                });
            } finally {
                this.loading=false;
            }
        },

        showPaymentModal(walletId) {
            this.selectedWalletId=walletId;
            this.$nextTick(() => {
                this.show("make-payment");
            });
        },

        show(modalName) {
            this.$modal.show(modalName);
        },

        onModalClosed() {
            this.selectedWalletId = false;
        },

        pendingPaymentRequest(paymentRequests) {
            return paymentRequests.filter((p) => p.state===null).length;
        },
    }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.chatbot-card-id {
    font-size: 14px;
    font-weight: 600;
    color: var(--gray-700);
}

.wallet-user-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-email {
    display: block;
    font-size: 12px;
    color: var(--gray-500);
    margin-top: 2px;
}

.wallet-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
}

.wallet-stat-item {
    padding: 12px;
    background: var(--gray-50);
    border-radius: 8px;
    border: 1px solid var(--gray-200);
}

.stat-value {
    font-size: 18px;
    font-weight: 700;
    margin-top: 4px;
}

.revenue-value {
    color: var(--blue-600);
}

.balance-value {
    color: var(--green-600);
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
}

.status-success {
    background: var(--green-50);
    color: var(--green-700);
    border: 1px solid var(--green-200);
}

.status-warning {
    background: var(--orange-50);
    color: var(--orange-700);
    border: 1px solid var(--orange-200);
}

.status-badge i {
    font-size: 16px;
}

.admin-toolbar-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.toolbar-stat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--gray-600);
    font-weight: 500;
}

.toolbar-stat i {
    font-size: 18px;
    color: var(--blue-600);
}

@media (max-width: 768px) {
    .wallet-stats {
        grid-template-columns: 1fr;
    }

    .admin-toolbar-info {
        display: none;
    }
}
</style>